import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Pencil, Trash2, Loader2, Tag, Calendar as CalendarIcon, Percent, Euro } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminCoupons() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: 10,
    min_order_amount: null,
    max_uses: null,
    max_uses_per_user: 1,
    valid_from: null,
    valid_until: null,
    is_active: true,
    applicable_plans: null
  });

  useEffect(() => {
    if (token) {
      fetchCoupons();
    }
  }, [token]);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCoupon(null);
    setFormData({
      code: "",
      description: "",
      discount_type: "percentage",
      discount_value: 10,
      min_order_amount: null,
      max_uses: null,
      max_uses_per_user: 1,
      valid_from: null,
      valid_until: null,
      is_active: true,
      applicable_plans: null
    });
    setShowDialog(true);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description || "",
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_order_amount: coupon.min_order_amount,
      max_uses: coupon.max_uses,
      max_uses_per_user: coupon.max_uses_per_user,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from) : null,
      valid_until: coupon.valid_until ? new Date(coupon.valid_until) : null,
      is_active: coupon.is_active,
      applicable_plans: coupon.applicable_plans
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.code) {
      toast.error("Inserisci un codice");
      return;
    }

    setSaving(true);
    try {
      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.coupon_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Coupon aggiornato");
      } else {
        await axios.post(`${API}/admin/coupons`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Coupon creato");
      }
      setShowDialog(false);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Eliminare il coupon ${coupon.code}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/coupons/${coupon.coupon_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coupon eliminato");
      fetchCoupons();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const generateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Codici <span className="text-primary">Sconto</span>
            </h1>
            <p className="text-sm text-muted-foreground">Gestisci i coupon e le promozioni</p>
          </div>
          <Button onClick={handleCreate} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Coupon
          </Button>
        </div>

        {/* Coupons List */}
        <div className="card-dashboard">
          {coupons.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nessun coupon creato</p>
              <Button onClick={handleCreate} variant="outline" className="mt-4">
                Crea il primo coupon
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium text-sm">Codice</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Sconto</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Utilizzi</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Validità</th>
                    <th className="text-left py-3 px-4 font-medium text-sm">Stato</th>
                    <th className="text-right py-3 px-4 font-medium text-sm">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.coupon_id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <span className="font-mono font-bold">{coupon.code}</span>
                        {coupon.description && (
                          <p className="text-xs text-muted-foreground">{coupon.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {coupon.discount_type === "percentage" ? (
                            <><Percent className="h-4 w-4 text-primary" /> {coupon.discount_value}%</>
                          ) : (
                            <><Euro className="h-4 w-4 text-primary" /> {coupon.discount_value}</>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono">
                          {coupon.times_used}{coupon.max_uses ? `/${coupon.max_uses}` : ""}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {coupon.valid_until ? (
                          <span>{format(new Date(coupon.valid_until), "dd/MM/yyyy", { locale: it })}</span>
                        ) : (
                          <span className="text-muted-foreground">Nessuna scadenza</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={coupon.is_active ? "default" : "secondary"}>
                          {coupon.is_active ? "Attivo" : "Disattivo"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(coupon)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(coupon)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? "Modifica Coupon" : "Nuovo Coupon"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="input-label">Codice</label>
              <div className="flex gap-2">
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SCONTO10"
                />
                <Button variant="outline" onClick={generateCode} type="button">
                  Genera
                </Button>
              </div>
            </div>

            <div>
              <label className="input-label">Descrizione</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione del coupon"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Tipo Sconto</label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentuale (%)</SelectItem>
                    <SelectItem value="fixed">Importo Fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="input-label">Valore</label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Ordine Minimo (€)</label>
                <Input
                  type="number"
                  value={formData.min_order_amount || ""}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="Nessun minimo"
                />
              </div>
              <div>
                <label className="input-label">Utilizzi Max</label>
                <Input
                  type="number"
                  value={formData.max_uses || ""}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Illimitati"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Valido Dal</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.valid_from ? format(formData.valid_from, "dd/MM/yyyy") : "Subito"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.valid_from}
                      onSelect={(d) => setFormData({ ...formData, valid_from: d })}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="input-label">Valido Fino Al</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.valid_until ? format(formData.valid_until, "dd/MM/yyyy") : "Sempre"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.valid_until}
                      onSelect={(d) => setFormData({ ...formData, valid_until: d })}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <label htmlFor="is_active" className="text-sm">Coupon attivo</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCoupon ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
